<template>
  <div class="container-fluid">
    <div class="page-wrapper">
      <div slot="header" class="page-wrapper-header">
        <div class="row">
          <div class="col-6">
            <h3 class="mb-0">{{ $t("NUVEI_CARDS.NUVEI_CARDS_LIST") }}</h3>
          </div>
          <div class="col-6 text-right">
            <base-button
              class="elite-button add"
              icon
              size="sm"
              @click="openNuveiCardCreateModal"
              v-if="$currentUserCan($permissions.PERM_CREATE_NUVEI_CARDS)"
            >
              <span class="btn-inner--icon">
                <i class="far fa-plus-circle"></i>
              </span>
              <span class="btn-inner--text">{{
                $t("NUVEI_CARDS.ADD_NUVEI_CARD")
              }}</span>
            </base-button>
            <notification-subscription
              v-if="$currentUserCan($permissions.PERM_VIEW_NUVEI_CARDS)"
              :objectType="'nuveiCards'"
              :events="{
                CREATE: $t('NOTIFICATIONS.EVENT_CREATE'),
                UPDATE: $t('NOTIFICATIONS.EVENT_UPDATE'),
                DELETE: $t('NOTIFICATIONS.EVENT_DELETE'),
              }"
            />
          </div>
        </div>
      </div>

      <nuvei-card-list-table
        @onViewNuveiCard="openNuveiCardViewModal"
        @onEditNuveiCard="openNuveiCardEditModal"
        @onDeleteNuveiCard="deleteNuveiCard"
        :key="renderKey * 100"
      />

      <div
        v-if="isViewNuveiCardModalOpened"
        class="resizable-wrapper"
        v-bind:class="[isViewNuveiCardModalOpened ? 'show' : 'hide']"
        :key="renderKey * 200"
      >
        <div
          class="resizable-wrapper-content"
          v-resizable-modal="'NUVEI_CARD_VIEW'"
        >
          <div class="resizable-wrapper-content-header">
            <div class="resizable-wrapper-content-header-left">
              <h1>
                {{ $t("NUVEI_CARDS.VIEW_NUVEI_CARD") }}
              </h1>
            </div>
            <div class="resizable-wrapper-content-header-right">
              <notification-subscription
                v-if="openNuveiCard"
                :objectType="'nuveiCards'"
                :objectId="openNuveiCard.id"
                :events="{
                  UPDATE: $t('NOTIFICATIONS.EVENT_UPDATE'),
                  DELETE: $t('NOTIFICATIONS.EVENT_DELETE'),
                }"
              />
              <base-dropdown
                title-classes="btn dropdown-button"
                menu-on-right
                :has-toggle="false"
              >
                <template slot="title">
                  <i class="fas fa-ellipsis-v"></i>
                </template>
                <button class="delete" @click="deleteNuveiCard(openNuveiCard)">
                  <i class="fal fa-trash-alt"></i>
                  <span>{{ $t("COMMON.DELETE") }}</span>
                </button>
              </base-dropdown>
              <button class="close" @click="closeNuveiCardModal">
                <i class="fal fa-times"></i>
              </button>
            </div>
          </div>
          <div class="resizable-wrapper-content-body">
            <view-nuvei-card-component
              v-if="openNuveiCard"
              :nuveiCardId="openNuveiCard.id"
            />
          </div>
        </div>
      </div>

      <div
        v-if="isEditNuveiCardModalOpened"
        class="resizable-wrapper"
        v-bind:class="[isEditNuveiCardModalOpened ? 'show' : 'hide']"
        :key="renderKey * 300"
      >
        <div class="resizable-wrapper-content" v-resizable-modal="'NUVEI_CARD'">
          <div class="resizable-wrapper-content-header">
            <div class="resizable-wrapper-content-header-left">
              <h1>
                {{ $t("NUVEI_CARDS.EDIT_NUVEI_CARD") }}
              </h1>
            </div>
            <div class="resizable-wrapper-content-header-right">
              <base-dropdown
                title-classes="btn dropdown-button"
                menu-on-right
                :has-toggle="false"
              >
                <template slot="title">
                  <i class="fas fa-ellipsis-v"></i>
                </template>
                <button
                  class="view"
                  @click="openNuveiCardViewModal(openNuveiCard)"
                >
                  <i class="fal fa-expand-alt"></i>
                  <span>{{ $t("COMMON.VIEW") }}</span>
                </button>
              </base-dropdown>
              <button class="close" @click="closeNuveiCardModal">
                <i class="fal fa-times"></i>
              </button>
            </div>
          </div>
          <div class="resizable-wrapper-content-body">
            <edit-nuvei-card-component
              v-if="openNuveiCard"
              :nuveiCardId="openNuveiCard.id"
              @onViewNuveiCard="openNuveiCardViewModal"
            />
          </div>
        </div>
      </div>

      <div
        v-if="isAddNuveiCardModalOpened"
        class="resizable-wrapper"
        v-bind:class="[isAddNuveiCardModalOpened ? 'show' : 'hide']"
        :key="renderKey * 400"
      >
        <div class="resizable-wrapper-content" v-resizable-modal="'NUVEI_CARD'">
          <div class="resizable-wrapper-content-header">
            <div class="resizable-wrapper-content-header-left">
              <h1>
                {{ $t("NUVEI_CARDS.ADD_NUVEI_CARD") }}
              </h1>
            </div>
            <div class="resizable-wrapper-content-header-right">
              <button class="close" @click="closeNuveiCardModal">
                <i class="fal fa-times"></i>
              </button>
            </div>
          </div>
          <div class="resizable-wrapper-content-body">
            <add-nuvei-card-component
              @onViewNuveiCard="openNuveiCardViewModal"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import swal from "sweetalert2";
import { Button } from "element-ui";
import requestErrorMixin from "@/mixins/request-error-mixin";
import {
  QUERY_ACTIONS_VIEW,
  QUERY_ACTIONS_EDIT,
  QUERY_ACTIONS_ADD,
} from "@/constants/common";
import NotificationSubscription from "@/components/NotificationSubscription.vue";
import NuveiCardListTable from "./partials/NuveiCardListTable.vue";
import EditNuveiCardComponent from "./components/EditNuveiCardComponent.vue";
import AddNuveiCardComponent from "./components/AddNuveiCardComponent.vue";
import ViewNuveiCardComponent from "./components/ViewNuveiCardComponent.vue";

export default {
  layout: "DashboardLayout",

  components: {
    NuveiCardListTable,
    NotificationSubscription,
    EditNuveiCardComponent,
    AddNuveiCardComponent,
    ViewNuveiCardComponent,
    [Button.name]: Button,
  },

  mixins: [requestErrorMixin],

  computed: {},

  watch: {},

  data() {
    const nuveiCardId = this.$route.query.id;
    const action = this.$route.query.action;
    let isViewNuveiCardModalOpened = false;
    let isEditNuveiCardModalOpened = false;
    let isAddNuveiCardModalOpened = false;
    let openNuveiCard = null;
    if (nuveiCardId && action) {
      if (action === QUERY_ACTIONS_VIEW) {
        isViewNuveiCardModalOpened = true;
      } else if (action === QUERY_ACTIONS_EDIT) {
        isEditNuveiCardModalOpened = true;
      }
      openNuveiCard = { id: nuveiCardId };
    } else if (action === QUERY_ACTIONS_ADD) {
      isAddNuveiCardModalOpened = true;
    }
    return {
      isViewNuveiCardModalOpened: isViewNuveiCardModalOpened,
      isEditNuveiCardModalOpened: isEditNuveiCardModalOpened,
      isAddNuveiCardModalOpened: isAddNuveiCardModalOpened,
      openNuveiCard: openNuveiCard,
      renderKey: 1,
    };
  },

  methods: {
    openNuveiCardCreateModal() {
      this.closeNuveiCardModal();
      this.isAddNuveiCardModalOpened = true;

      history.pushState(
        {},
        null,
        this.$router.resolve({
          name: "List NuveiCards",
          query: { action: QUERY_ACTIONS_ADD },
        }).href
      );
    },
    openNuveiCardViewModal(nuveiCard, reRender = false) {
      this.closeNuveiCardModal();
      this.openNuveiCard = nuveiCard;
      this.isViewNuveiCardModalOpened = true;
      if (reRender) {
        this.renderKey++;
      }

      history.pushState(
        {},
        null,
        this.$router.resolve({
          name: "List NuveiCards",
          query: { id: this.openNuveiCard.id, action: QUERY_ACTIONS_VIEW },
        }).href
      );
    },
    openNuveiCardEditModal(nuveiCard) {
      this.closeNuveiCardModal();
      this.openNuveiCard = nuveiCard;
      this.isEditNuveiCardModalOpened = true;

      history.pushState(
        {},
        null,
        this.$router.resolve({
          name: "List NuveiCards",
          query: { id: this.openNuveiCard.id, action: QUERY_ACTIONS_EDIT },
        }).href
      );
    },
    closeNuveiCardModal() {
      this.isAddNuveiCardModalOpened = false;
      this.isViewNuveiCardModalOpened = false;
      this.isEditNuveiCardModalOpened = false;
      this.openNuveiCard = null;

      history.pushState(
        {},
        null,
        this.$router.resolve({
          name: "List NuveiCards",
          query: {},
        }).href
      );
    },

    async deleteNuveiCard(nuveiCard) {
      const confirmation = await swal.fire({
        title: this.$t("NUVEI_CARDS.DELETE_THIS_NUVEI_CARD"),
        type: "question",
        buttonsStyling: false,
        showCancelButton: true,
        confirmButtonText: this.$t("COMMON.YES"),
        cancelButtonText: this.$t("COMMON.NO"),
        confirmButtonClass: "btn btn-primary",
        cancelButtonClass: "btn btn-warning",
      });

      try {
        if (confirmation.value === true) {
          await this.$store.dispatch("nuveiCards/destroy", nuveiCard.id);
          this.renderKey++;
          this.closeNuveiCardModal();
          this.$notify({
            type: "success",
            message: this.$t("NUVEI_CARDS.NUVEI_CARD_DELETED"),
          });
        }
      } catch (error) {
        await this.showRequestError(error);
        this.$notify({
          type: "danger",
          message: this.$t("ERRORS.SOMETHING_WENT_WRONG"),
        });
      }
    },
  },
};
</script>
